import React, { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import ErrorBoundary from "@/components/ErrorBoundary";
import BackendStatusBanner from "@/components/BackendStatusBanner";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Pipeline from "@/pages/Pipeline";
import Proposals from "@/pages/Proposals";
import Forms from "@/pages/Forms";
import Documents from "@/pages/Documents";
import RfqQuote from "@/pages/RfqQuote";
import DealDetail from "@/pages/DealDetail";
import POs from "@/pages/POs";
import Discover from "@/pages/Discover";
import Takeoff from "@/pages/Takeoff";
import RunView from "@/pages/RunView";
import Approval from "@/pages/Approval";
import DataLibrary from "@/pages/DataLibrary";
import Admin from "@/pages/Admin";
import Settings from "@/pages/Settings";
import RecordView from "@/pages/RecordView";

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const res = await api.post("/auth/session", {}, { headers: { "X-Session-ID": sessionId } });
        // Persist the session token as a localStorage Bearer fallback (browsers that drop the cross-site
        // cookie), then set the user without leaking the token into React state.
        const { session_token, ...u } = res.data || {};
        if (session_token) { try { window.localStorage.setItem("wds_token", session_token); } catch { /* ignore */ } }
        setUser(u);
        window.history.replaceState(null, "", "/dashboard");
        navigate("/dashboard", { replace: true, state: { user: u } });
      } catch {
        navigate("/", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-zinc-300 font-mono text-sm">
      <span className="animate-pulse">Establishing secure session…</span>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground font-mono text-sm">
        <span className="animate-pulse">Loading…</span>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  // Dir 65 — roles flattened: any authenticated employee has full access to every screen.
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/rfq/:token" element={<RfqQuote />} />
      <Route path="/dashboard" element={<ProtectedRoute><Layout><Home /></Layout></ProtectedRoute>} />
      <Route path="/pipeline" element={<ProtectedRoute><Layout><Pipeline /></Layout></ProtectedRoute>} />
      <Route path="/proposals/federal" element={<ProtectedRoute><Layout><Proposals type="Federal" /></Layout></ProtectedRoute>} />
      <Route path="/proposals/commercial" element={<ProtectedRoute><Layout><Proposals type="Commercial" /></Layout></ProtectedRoute>} />
      <Route path="/deals/:id" element={<ProtectedRoute><Layout><DealDetail /></Layout></ProtectedRoute>} />
      <Route path="/pos" element={<ProtectedRoute><Layout><POs /></Layout></ProtectedRoute>} />
      <Route path="/contacts" element={<Navigate to="/library?tab=people" replace />} />
      <Route path="/discover" element={<ProtectedRoute><Layout><Discover /></Layout></ProtectedRoute>} />
      <Route path="/takeoff" element={<ProtectedRoute><Layout><Takeoff /></Layout></ProtectedRoute>} />
      <Route path="/forms" element={<ProtectedRoute><Layout><Forms /></Layout></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><Layout><Documents /></Layout></ProtectedRoute>} />
      <Route path="/bids/:id" element={<ProtectedRoute><Layout><RunView /></Layout></ProtectedRoute>} />
      <Route path="/bids/:id/approve" element={<ProtectedRoute><Layout><Approval /></Layout></ProtectedRoute>} />
      <Route path="/company" element={<Navigate to="/library?tab=company" replace />} />
      <Route path="/library" element={<ProtectedRoute><Layout><DataLibrary /></Layout></ProtectedRoute>} />
      <Route path="/brain" element={<Navigate to="/library" replace />} />
      <Route path="/settings" element={<ProtectedRoute><Layout><Settings /></Layout></ProtectedRoute>} />
      <Route path="/r/:otype/:id" element={<ProtectedRoute><Layout><RecordView /></Layout></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><Layout><Admin /></Layout></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <BackendStatusBanner />
          <AppRouter />
          <Toaster position="top-right" duration={3000} />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
